import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Check, RefreshCw } from "lucide-react";
import { Button, Field, Textarea, Select } from "@/design/components";
import { useChangeZeusProjectStatus } from "@/domain/useProjectCreation";
import { ENTITY_STATE } from "@/domain/status";

// ZEUS-142 — direct status toggle, stratus_admin only (server enforces; the
// caller decides whether to render the trigger — see
// ProjectManagementView.tsx). Free choice among all 3 statuses: the backend
// (server/routers/zeusProject.js) has no adjacency restriction, so this
// mirrors that rather than imposing an arbitrary UI-only sequence.

const TOGGLEABLE_STATUSES = ["draft", "review", "published"] as const;
type ToggleableStatus = (typeof TOGGLEABLE_STATUSES)[number];

export function ChangeProjectStatusModal({
  open,
  onOpenChange,
  projectId,
  currentStatus,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Raw id — real numeric-string in real-API mode, demo id in demo mode. */
  projectId: string;
  currentStatus: string;
  onChanged?: () => void;
}) {
  const { changeZeusProjectStatus, isPending, error } = useChangeZeusProjectStatus();

  const [status, setStatus] = useState<ToggleableStatus>(
    (TOGGLEABLE_STATUSES as readonly string[]).includes(currentStatus)
      ? (currentStatus as ToggleableStatus)
      : "draft",
  );
  const [justification, setJustification] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = justification.trim().length > 0 && status !== currentStatus;

  function reset() {
    setStatus(
      (TOGGLEABLE_STATUSES as readonly string[]).includes(currentStatus)
        ? (currentStatus as ToggleableStatus)
        : "draft",
    );
    setJustification("");
    setSubmitted(false);
  }

  async function onSubmit() {
    if (!canSubmit) return;
    const result = await changeZeusProjectStatus(projectId, { status, justification: justification.trim() });
    if (!result) return;
    setSubmitted(true);
    onChanged?.();
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in">
          <div className="flex items-start justify-between gap-3 p-6 pb-3">
            <Dialog.Title className="text-base font-semibold tracking-tight text-ink">
              {submitted ? "Status changed" : "Change project status"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Close" className="rounded-md p-1 text-faint hover:bg-subtle hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {submitted ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <div className="flex items-start gap-3 rounded-md border border-ok/30 bg-okSoft px-3 py-3 text-sm text-ok">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Status changed to <strong>{ENTITY_STATE[status]?.label ?? status}</strong>.</span>
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              <Dialog.Description className="text-sm leading-relaxed text-mute">
                Applies immediately — there's no separate review step for a direct status change.
              </Dialog.Description>

              <Field label="New status">
                <Select value={status} onChange={(e) => setStatus(e.target.value as ToggleableStatus)}>
                  {TOGGLEABLE_STATUSES.map((s) => (
                    <option key={s} value={s}>{ENTITY_STATE[s]?.label ?? s}</option>
                  ))}
                </Select>
              </Field>

              <Field label="Justification">
                <Textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={3}
                  placeholder="Why is this status change necessary?"
                />
              </Field>

              {error && <p role="alert" className="text-sm text-bad">{error}</p>}
            </div>
          )}

          {!submitted && (
            <div className="flex items-center justify-end gap-2 border-t border-line p-6 pt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancel</Button>
              </Dialog.Close>
              <Button disabled={!canSubmit || isPending} onClick={onSubmit}>
                <RefreshCw className="h-4 w-4" /> {isPending ? "Changing…" : "Change status"}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
